import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getEndpointCacheKey } from '../api/config';
import { useEffect } from 'react';

// Unified query key prefix (not client caching — gcTime/staleTime are zero)
const UNIFIED_CACHE_KEY = ['wms_data'];

const QUERY_DEFAULTS = {
  staleTime: 5 * 60 * 1000, // 5 minutes (caching active)
  gcTime: 10 * 60 * 1000, // 10 minutes (keep in memory on unmount)
  refetchInterval: false,
  refetchOnMount: true, // Use standard caching/refetching on mount
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
  retry: 1,
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
  throwOnError: false,
};

// Create a custom event for logout
const LOGOUT_EVENT = 'app:logout';

// Removed global prefetch; all fetching is now on-demand

export const useCachedData = (endpoint, customConfig = {}) => {
  const queryClient = useQueryClient();

  // Set up global refresh mechanism and token change listener
  useEffect(() => {
    // Clear React Query when token is removed (logout)
    const handleTokenChange = () => {
      const token = sessionStorage.getItem('token');
      
      // If no token, clear all cache
      if (!token) {
        queryClient.clear();
        return;
      }
    };

    // Initial check
    handleTokenChange();

    // Set up storage event listener for token changes
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        handleTokenChange();
      }
    };

    // Set up custom logout event listener
    const handleLogout = () => {
      queryClient.clear();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(LOGOUT_EVENT, handleLogout);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(LOGOUT_EVENT, handleLogout);
    };
  }, [queryClient]);

  const endpointKey = getEndpointCacheKey(endpoint);

  const mergedConfig = {
    ...QUERY_DEFAULTS,
    ...customConfig,
  };

  const queryKey = [...UNIFIED_CACHE_KEY, endpointKey];

  return useQuery({
    queryKey,
    queryFn: async () => {
      try {
        if (import.meta.env.DEV) {
          console.log(`Fetching ${endpointKey}…`);
        }
        const response = await api.get(endpoint);
        const payload = response.data?.data ?? response.data;
        if (import.meta.env.DEV) {
          const summary = Array.isArray(payload)
            ? `rows=${payload.length}`
            : payload && typeof payload === 'object'
              ? `keys=${Object.keys(payload).length}`
              : String(typeof payload);
          console.log(`OK ${endpointKey} (${summary})`);
        }
        sessionStorage.setItem('lastFetchTime', Date.now().toString());
        return payload;
      } catch (error) {
        console.error(`Fetch ${endpointKey}:`, error?.message || error);
        throw error;
      }
    },
    ...mergedConfig,
    enabled: mergedConfig.enabled ?? true,
  });
};

// Export the logout event for use in other components
export const triggerLogout = () => {
  window.dispatchEvent(new Event(LOGOUT_EVENT));
}; 